import React, { useState, useCallback } from "react";
import { useT } from "@transifex/react";
import styled from "styled-components";
import { RendererProps } from "./index";
import { TextInput } from "./renderer-styles";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const InputsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const MismatchHint = styled.p`
  margin: 0;
  font-size: 14px;
  color: #dc2626;
`;

function isEmailAnswerValid(
  primary: string,
  confirm: string,
  required: boolean | undefined,
  requireConfirmation: boolean
): boolean {
  const trimmedPrimary = primary.trim();
  if (trimmedPrimary.length === 0) {
    return !required;
  }
  if (!EMAIL_PATTERN.test(primary)) {
    return false;
  }
  if (requireConfirmation) {
    return primary === confirm;
  }
  return true;
}

const EmailInputRenderer: React.FC<RendererProps> = ({
  question,
  answer,
  onAnswer,
}) => {
  const t = useT();
  const requireConfirmation = Boolean(question.confirmation_label?.trim());

  const [value, setValue] = useState(() =>
    answer?.value != null ? String(answer.value) : ""
  );
  const [confirmValue, setConfirmValue] = useState(() =>
    requireConfirmation ? answer?.value ?? "" : ""
  );

  const emit = useCallback(
    (primary: string, confirm: string) => {
      onAnswer(
        primary,
        isEmailAnswerValid(
          primary,
          confirm,
          question.required,
          requireConfirmation
        )
      );
    },
    [onAnswer, question.required, requireConfirmation]
  );

  const handlePrimaryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setValue(val);
      emit(val, confirmValue);
    },
    [emit, confirmValue]
  );

  const handleConfirmChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setConfirmValue(val);
      emit(value, val);
    },
    [emit, value]
  );

  const showMismatch =
    requireConfirmation && confirmValue.length > 0 && value !== confirmValue;

  return (
    <InputsColumn>
      <TextInput
        type="email"
        value={value}
        onChange={handlePrimaryChange}
        placeholder={question.placeholder || t("email@example.com")}
      />
      {requireConfirmation && (
        <>
          <TextInput
            type="email"
            value={confirmValue}
            onChange={handleConfirmChange}
            placeholder={question.confirmation_label || t("Confirm email")}
            aria-invalid={showMismatch}
          />
          {showMismatch && (
            <MismatchHint>{t("Email addresses must match")}</MismatchHint>
          )}
        </>
      )}
    </InputsColumn>
  );
};

export default EmailInputRenderer;
